import { useEffect, useId, useRef, useState } from "react";

interface MermaidDiagramProps {
  chart: string;
  className?: string;
  /** Force a specific mermaid theme instead of auto-detecting from dark mode */
  forceTheme?: "default" | "dark";
}

// ── Singleton render queue ──
// Mermaid's initialize() and render() modify global state.
// When multiple diagrams mount simultaneously, concurrent calls race each other
// causing diagrams to flicker or disappear. This queue serializes all render
// operations so only one diagram renders at a time.

let renderQueue: Promise<void> = Promise.resolve();
let lastInitTheme: string | null = null;

async function queueMermaidRender(
  renderId: string,
  chart: string,
  theme: string,
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    renderQueue = renderQueue.then(async () => {
      try {
        const mermaid = (await import("mermaid")).default;

        // Only re-initialize if theme changed — avoids resetting state unnecessarily
        if (lastInitTheme !== theme) {
          mermaid.initialize({
            startOnLoad: false,
            theme: theme as "default" | "dark",
            fontFamily: '"Nunito", "Segoe UI", Roboto, sans-serif',
            securityLevel: "strict",
            themeVariables: {
              background: "transparent",
            },
          });
          lastInitTheme = theme;
        }

        const { svg } = await mermaid.render(renderId, chart.trim());
        resolve(svg);
      } catch (err) {
        reject(err);
      }
    });
  });
}

export function MermaidDiagram({ chart, className, forceTheme }: MermaidDiagramProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const uniqueId = useId().replace(/:/g, "_");

  useEffect(() => {
    let cancelled = false;

    const renderId = `mermaid_${uniqueId}`;

    function cleanupMermaidDOM() {
      const orphan = document.getElementById(renderId);
      if (orphan && !containerRef.current?.contains(orphan)) {
        orphan.remove();
      }
      const dWrapper = document.getElementById(`d${renderId}`);
      if (dWrapper && !containerRef.current?.contains(dWrapper)) {
        dWrapper.remove();
      }
    }

    async function renderChart() {
      try {
        const isDark = document.documentElement.classList.contains("dark");
        const theme = forceTheme ?? (isDark ? "dark" : "default");

        const renderedSvg = await queueMermaidRender(renderId, chart, theme);

        if (!cancelled) {
          setSvg(renderedSvg);
          setError(null);
        }
      } catch (err) {
        cleanupMermaidDOM();
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to render diagram",
          );
        }
      }
    }

    renderChart();
    return () => {
      cancelled = true;
      cleanupMermaidDOM();
    };
  }, [chart, uniqueId, forceTheme]);

  if (error) {
    return (
      <div className="mermaid-error">
        <p className="mermaid-error-title">Diagram Error</p>
        <pre className="mermaid-error-message">{error}</pre>
      </div>
    );
  }

  if (!svg) {
    return (
      <div className="mermaid-loading">
        <span>Rendering diagram...</span>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`mermaid-container ${className || ""}`}
      data-mermaid="true"
      // Mermaid generates SVG programmatically from parsed diagram syntax — not untrusted user HTML.
      // Mermaid has its own built-in XSS protection (securityLevel). DOMPurify strips foreignObject
      // content and attributes that Mermaid needs for text labels, so we skip sanitization here.
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
